import { BingoItem, ClaimStatus } from '@bst-stellar/shared';

interface BingoCardProps {
    gridData: string[][]; // 2D array of item IDs
    items: BingoItem[]; // All available items
    claims: any[]; // User's claims for this card
    onClaimClick: (itemId: string, position: { row: number; col: number }) => void;
    readOnly?: boolean;
}

export default function BingoCard({
    gridData,
    items,
    claims,
    onClaimClick,
    readOnly = false,
}: BingoCardProps) {
    const itemsMap = new Map(items.map((item) => [item.id, item]));
    // Build claims map - for each position, prioritize approved > pending > rejected
    const claimsMap = new Map<string, any>();
    claims
        .slice() // Don't mutate original
        .sort((a, b) => {
            // Sort by status priority: approved (3) > pending (2) > rejected (1)
            const priority: Record<string, number> = { approved: 3, pending: 2, rejected: 1 };
            return (priority[b.status] || 0) - (priority[a.status] || 0);
        })
        .forEach((claim) => {
            const key = `${claim.position.row},${claim.position.col}`;
            // Only set if not already set (keeps highest priority)
            if (!claimsMap.has(key)) {
                claimsMap.set(key, claim);
            }
        });

    function getSquareStatus(row: number, col: number) {
        const key = `${row},${col}`;
        const claim = claimsMap.get(key);
        if (!claim) return 'empty';
        return claim.status;
    }

    function getSquareClass(status: string) {
        switch (status) {
            case ClaimStatus.APPROVED:
                return 'bg-green-100 border-green-500 text-green-900';
            case ClaimStatus.PENDING:
                return 'bg-yellow-100 border-yellow-500 text-yellow-900';
            case ClaimStatus.REJECTED:
                return 'bg-red-100 border-red-500 text-red-900 hover:border-red-600 cursor-pointer';
            default:
                return 'bg-surface border-border hover:border-primary cursor-pointer text-text-primary';
        }
    }

    return (
        <div className="inline-block">
            <div className="grid grid-cols-5 gap-2 p-4 bg-gray-50 rounded-lg">
                {gridData.map((row, rowIndex) =>
                    row.map((itemId, colIndex) => {
                        const item = itemsMap.get(itemId);
                        const status = getSquareStatus(rowIndex, colIndex);
                        // Allow clicking: empty cells, rejected claims (for resubmission), and pending claims (for cancellation)
                        const isClickable = !readOnly && (status === 'empty' || status === ClaimStatus.REJECTED || status === ClaimStatus.PENDING);

                        return (
                            <div
                                key={`${rowIndex}-${colIndex}`}
                                className={`
                  relative w-32 h-32 p-3 border-2 rounded-lg
                  flex flex-col items-center justify-center text-center
                  transition-all duration-200
                  ${getSquareClass(status)}
                  ${isClickable ? 'hover:shadow-md' : ''}
                `}
                                onClick={() => {
                                    if (isClickable && item) {
                                        onClaimClick(item.id, { row: rowIndex, col: colIndex });
                                    }
                                }}
                            >
                                {item && (
                                    <>
                                        <div className="text-xs font-semibold line-clamp-3">
                                            {item.title}
                                        </div>
                                        {status !== 'empty' && (
                                            <div className="absolute bottom-1 right-1">
                                                {status === ClaimStatus.APPROVED && (
                                                    <span className="text-success text-xl">✓</span>
                                                )}
                                                {status === ClaimStatus.PENDING && (
                                                    <span className="text-yellow-600 text-xl">⏱</span>
                                                )}
                                                {status === ClaimStatus.REJECTED && (
                                                    <span className="text-red-600 text-xl">✗</span>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
